import { createAsyncThunk } from '@reduxjs/toolkit';
import AuthAPI from "../../api/authAPI";
import { 
  loginStart, 
  loginSuccess, 
  loginFailure,
  registerStart,
  registerSuccess,
  registerFailure,
  logout as logoutAction,
  refreshToken as refreshTokenAction
} from '../reducers/auth';
import { setErrorMessage } from '../reducers/message';

// Async thunk for user registration
export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async ({ email, password }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(registerStart());
      // 1. Get CSRF token and set cookie
      const csrfToken = await AuthAPI.getCsrfToken();
      // 2. Send register with CSRF header and credentials
      await AuthAPI.register(email, password, csrfToken);
      // 3. Fetch user info
      const user = await AuthAPI.getCurrentUser();
      dispatch(registerSuccess());
      return user;
    } catch (error) {
      const message = 
        error.response?.data?.message || 
        error.message || 
        'Registration failed';
      dispatch(registerFailure({ error: message }));
      dispatch(setErrorMessage(message));
      return rejectWithValue(message);
    }
  }
);

// Async thunk for user login
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(loginStart());
      // 1. Get CSRF token and set cookie
      const csrfToken = await AuthAPI.getCsrfToken();
      // 2. Send login with CSRF header and credentials
      await AuthAPI.login(email, password, csrfToken);
      // 3. Fetch user info
      const user = await AuthAPI.getCurrentUser();
      dispatch(loginSuccess({ user }));
      return user;
    } catch (error) {
      const message = 
        error.response?.data?.detail || 
        error.response?.data?.message || 
        error.message || 
        'Login failed';
      dispatch(loginFailure({ error: message }));
      dispatch(setErrorMessage(message));
      return rejectWithValue(message);
    }
  }
);

// Async thunk for token refresh
export const refreshTokenAsync = createAsyncThunk(
  'auth/refreshToken',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await AuthAPI.refresh();
      // After refresh, fetch user info
      const user = await AuthAPI.getCurrentUser();
      dispatch(refreshTokenAction({ user }));
      return user;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Token refresh failed';
      dispatch(setErrorMessage(message));
      return rejectWithValue(message);
    }
  }
);

// Regular action for logout
export const logout = () => async (dispatch) => {
  const csrfToken = await AuthAPI.getCsrfToken();
  await AuthAPI.logout(csrfToken);
  dispatch(logoutAction());
};

// Legacy action creators for backward compatibility
export const legacyLogin = (email, password) => (dispatch) => {
  return dispatch(login({ email, password }));
};

export const legacyRegisterUser = (data) => (dispatch) => {
  return dispatch(registerUser({ email: data.email, password: data.password }));
};

// Export the refreshToken action directly from the slice for direct usage
export const refreshToken = refreshTokenAction;
